import { useState, useEffect, useCallback, Suspense } from "react";
import { useNavigate, useLocation, Navigate, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import AuthGuard from '@/components/guards/AuthGuard';
import RoleGuard from '@/components/guards/RoleGuard';
import PermissionGuard from '@/components/guards/PermissionGuard';
import { useAuthStore } from '@/stores/auth';
import { registerAuthRedirectHandler } from '@/services/auth-navigation';
import { useWebSocket } from '@/components/realtime/WebSocketProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { Button } from '@/components/ui/button';
import ErrorBoundary from '@/components/error/ErrorBoundary';
import LazyLoadingFallback from '@/components/lazy/LazyLoadingFallback';
import {
  LazyDashboard,
  LazyProjects,
  LazyWorkflowStages,
  LazyAdministration,
  LazyInterventions,
  LazyStageModal,
  LazyContextAnalytics,
  LazySystemRecommendations,
  LazyCostUsage,
  LazyUsageEfficiency,
  LazyProgramPgm,
  LazyReleaseWorkspace,
  LazyDiscoveryPage,
  LazyControlTower,
  LazyActors,
  LazyRepos,
  LazySkills,
  LazyWorkflows,
  LazyCacheManagement,
  LazyRepositorySyncStatus,
  LazyAudit,
  LazyConfig,
} from '@/components/lazy/LazyComponents';
import { lazy } from 'react';
const LazyTaskQueue = lazy(() => import('@/pages/TaskQueue'));
import { GlassSidebar } from '@/components/layout/GlassSidebar';
import InterventionNotification from '@/components/notifications/InterventionNotification';
import BrowserNotificationManager from '@/components/notifications/BrowserNotificationManager';
import InAppNotificationPopup from '@/components/notifications/InAppNotificationPopup';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import IdleWarningModal from '@/components/auth/IdleWarningModal';
import { savePgmPath } from '@/pages/new/pgm/pgmNavigation';

export default function App() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState("");
  const [stageFilters, setStageFilters] = useState<{
    project_ids?: string[];
    status?: string[];
    priority?: string[];
  }>({});
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (location.pathname.startsWith('/pgm')) {
      savePgmPath(location.pathname, location.search);
    }
  }, [location.pathname, location.search]);

  const { subscribeToMessage } = useWebSocket();
  const queryClient = useQueryClient();

  // AUTO-LOGOUT callbacks
  const handleIdleTimeout = useCallback(async () => {
    setShowIdleWarning(false);
    await logout();
    navigate('/login', { state: { reason: 'session_timeout' }, replace: true });
  }, [isAuthenticated, location.pathname, logout, navigate]);

  const handleIdleWarning = useCallback(() => {
    setShowIdleWarning(true);
  }, []);

  const handleIdleActive = useCallback(() => {
    setShowIdleWarning(false);
  }, []);

  // Idle/session policy (admin-configurable). Access-token renewal is handled
  // automatically in the auth store; this only governs the inactivity logout.
  const { data: sessionPolicy } = useQuery({
    queryKey: ['session-policy'],
    queryFn: () => apiClient.getSessionPolicy(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const policyLoaded = !!sessionPolicy;
  const idleLogoutEnabled = sessionPolicy?.idleLogoutEnabled !== false; // default ON
  const idleTimeoutMs = Math.max(5, sessionPolicy?.idleTimeoutMinutes ?? 30) * 60 * 1000;

  const { remainingTime, resetTimer } = useIdleTimeout({
    timeout: idleTimeoutMs,
    warningTimeout: 30 * 1000,
    // Gate on policyLoaded so the configured timeout is in effect before the
    // timer arms (the listener effect re-runs when `enabled` flips).
    enabled: isAuthenticated && location.pathname !== '/login' && policyLoaded && idleLogoutEnabled,
    onIdle: handleIdleTimeout,
    onWarning: handleIdleWarning,
    onActive: handleIdleActive
  });

  const handleStayLoggedIn = () => {
    setShowIdleWarning(false);
    resetTimer();
  };

  const handleLogoutNow = async () => {
    setShowIdleWarning(false);
    await logout();
    navigate('/login', { state: { reason: 'manual_logout' }, replace: true });
  };

  useEffect(() => {
    registerAuthRedirectHandler((path, opts) => navigate(path, opts));
    return () => registerAuthRedirectHandler(null);
  }, [navigate]);

  // Batched query invalidation for WebSocket events
  const invalidationQueue = new Set<string>();
  let invalidationTimeout: NodeJS.Timeout | null = null;

  const queueInvalidation = (queryKey: any[]) => {
    invalidationQueue.add(JSON.stringify(queryKey));
    if (invalidationTimeout) clearTimeout(invalidationTimeout);
    invalidationTimeout = setTimeout(() => {
      invalidationQueue.forEach(key => {
        queryClient.invalidateQueries({ queryKey: JSON.parse(key) });
      });
      invalidationQueue.clear();
    }, 50);
  };

  // Global WebSocket subscriptions
  useEffect(() => {
    const unsubscribeCreated = subscribeToMessage('stage_item_created', (event: any) => {
      const lower = event.stage?.toLowerCase();
      queueInvalidation(['stage-items', lower]);
      queueInvalidation(['stage-items-infinite', lower]);
    });
    const unsubscribeUpdated = subscribeToMessage('stage_item_updated', (event: any) => {
      const lower = event.stage?.toLowerCase();
      queueInvalidation(['stage-items', lower]);
      queueInvalidation(['stage-items-infinite', lower]);
    });
    const unsubscribeDeleted = subscribeToMessage('stage_item_deleted', (event: any) => {
      const lower = event.stage?.toLowerCase();
      queueInvalidation(['stage-items', lower]);
      queueInvalidation(['stage-items-infinite', lower]);
    });
    // DEFECT-WS-LATENCY-003: stage_started / stage_completed were only handled in
    // WorkflowStages / StageModal / Dashboard. On any other view (project list,
    // release view, kanban) a stage finishing produced no refresh until manual
    // reload. Wire them into the global invalidation layer so every view reacts.
    const unsubscribeStageStarted = subscribeToMessage('stage_started', (event: any) => {
      const lower = event.stage?.toLowerCase();
      queueInvalidation(['stage-items', lower]);
      queueInvalidation(['stage-items-infinite', lower]);
      queueInvalidation(['projects']);
    });
    const unsubscribeStageCompleted = subscribeToMessage('stage_completed', (event: any) => {
      const lower = event.stage?.toLowerCase();
      queueInvalidation(['stage-items', lower]);
      queueInvalidation(['stage-items-infinite', lower]);
      const nextLower = event.nextStage?.toLowerCase();
      if (nextLower) {
        queueInvalidation(['stage-items', nextLower]);
        queueInvalidation(['stage-items-infinite', nextLower]);
      }
      queueInvalidation(['projects']);
      queueInvalidation(['task-queue-stats']);
    });
    // DEFECT-WS-LATENCY-002: react to CI status changes globally (badges live in
    // stage items + CI_FAILURE interventions; no dedicated ci query key exists).
    const unsubscribeCiStatus = subscribeToMessage('ci_status_updated', () => {
      queueInvalidation(['stage-items']);
      queueInvalidation(['interventions']);
      queueInvalidation(['interventions-pending-keys']);
    });
    // Cluster B CD: deploy run state changes refresh the deploy timeline and the
    // DEPLOYMENT_APPROVAL intervention cards.
    const unsubscribeDeploymentRun = subscribeToMessage('deployment_run_updated', (event: any) => {
      queueInvalidation(event?.project_id ? ['deployment-runs', event.project_id] : ['deployment-runs']);
      queueInvalidation(['interventions']);
      queueInvalidation(['interventions-pending-keys']);
    });
    const unsubscribeJiraSync = subscribeToMessage('jira_sync_status', () => {
      queueInvalidation(['stage-items']);
    });
    const unsubscribeMondaySync = subscribeToMessage('monday_sync_status', () => {
      queueInvalidation(['stage-items']);
    });
    const unsubscribeWorkflowStarted = subscribeToMessage('workflow_started', () => {
      queueInvalidation(['projects']);
    });
    const unsubscribeWorkflowStatus = subscribeToMessage('workflow_status_changed', (event: any) => {
      queueInvalidation(['projects']);
      // FIX-DASH-STUCK-001: emitted alongside agent terminal status (COMPLETED/FAILED).
      // On terminal status, refresh the task-queue so Dashboard cards clear globally.
      const status = String(event?.status || '').toUpperCase();
      if (['COMPLETED', 'FAILED', 'FINISHED', 'ERROR', 'CANCELED', 'CANCELLED', 'EXPIRED'].includes(status)) {
        queueInvalidation(['task-queue']);
        queueInvalidation(['task-queue-stats']);
      }
    });
    const unsubscribeWorkflowComplete = subscribeToMessage('workflow_completed', () => {
      queueInvalidation(['projects']);
      queueInvalidation(['task-queue-stats']);
    });
    const unsubscribeInterventionRequired = subscribeToMessage('intervention_required', () => {
      queueInvalidation(['interventions']);
      // FIX-DASH-STUCK-001: a review gate is raised right after task_queue → COMPLETED,
      // so this is a reliable secondary completion signal for the task-queue cards.
      queueInvalidation(['task-queue']);
      queueInvalidation(['task-queue-stats']);
    });
    const unsubscribeInterventionResolved = subscribeToMessage('intervention_resolved', () => {
      queueInvalidation(['interventions']);
    });
    const unsubscribeTaskAssigned = subscribeToMessage('task_assigned', () => {
      queueInvalidation(['task-queue-stats']);
    });
    const unsubscribeTaskQueueUpdated = subscribeToMessage('task_queue_updated', () => {
      queueInvalidation(['task-queue-stats']);
      queueInvalidation(['task-queue']);
    });
    // system_alert → Zustand store wiring is in WebSocketProvider (runs before AuthGuard).
    // Here we only invalidate the system-health query for dashboard refresh.
    const unsubscribeSystemAlert = subscribeToMessage('system_alert', () => {
      queueInvalidation(['system-health']);
    });

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
      unsubscribeStageStarted();
      unsubscribeStageCompleted();
      unsubscribeCiStatus();
      unsubscribeDeploymentRun();
      unsubscribeJiraSync();
      unsubscribeMondaySync();
      unsubscribeWorkflowStarted();
      unsubscribeWorkflowStatus();
      unsubscribeWorkflowComplete();
      unsubscribeInterventionRequired();
      unsubscribeInterventionResolved();
      unsubscribeTaskAssigned();
      unsubscribeTaskQueueUpdated();
      unsubscribeSystemAlert();
    };
  }, []);

  // Stage modal handlers
  const handleOpenStage = (stageId: string, filters?: {
    project_ids?: string[];
    status?: string[];
    priority?: string[];
  }) => {
    setSelectedStage(stageId);
    setStageFilters(filters || {});
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <AuthGuard>
      <div className="h-screen flex bg-cosmic-mesh text-slate-800 dark:text-slate-200 antialiased overflow-hidden">
        {/* Ambient mesh overlay */}
        <div className="mesh-light" />

        {/* Glass Sidebar Navigation */}
        <GlassSidebar
          userInitials={user?.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || 'AU'}
          userName={user?.name || 'Admin User'}
          workspaceName="Fluent.ai Workspace"
          onSignOut={async () => {
            await logout();
            navigate('/login', { replace: true });
          }}
        />

        {/* Main Content Area.
            FIX-DASH-VIEWPORT-001: main is a flex COLUMN and the routed content
            owns the scroll, keeping `h-full` pages (e.g. the Dashboard) within
            the viewport. System alerts now live in the Dashboard's "Needs
            Attention" card rather than a global banner. */}
        <main className="flex-1 h-screen flex flex-col overflow-hidden relative">
          <div className="flex-1 min-h-0 overflow-y-auto">
          <ErrorBoundary fallback={<div className="text-center py-8 text-red-600 dark:text-red-400">Something went wrong. Please refresh the page.</div>}>
            <Suspense fallback={<LazyLoadingFallback message="Loading..." />}>
              <Routes>
                {/* Root redirect */}
                <Route path="/" element={<Navigate to="/control-tower" replace />} />

                {/* Core screens */}
                <Route path="/dashboard" element={
                  <ErrorBoundary fallback={<div className="text-center py-8 text-red-600">Dashboard failed to load.</div>}>
                    <LazyDashboard />
                  </ErrorBoundary>
                } />
                <Route path="/projects" element={
                  <ErrorBoundary fallback={<div className="text-center py-8 text-red-600">Projects failed to load.</div>}>
                    <LazyProjects />
                  </ErrorBoundary>
                } />
                <Route path="/workflow" element={
                  <ErrorBoundary fallback={<div className="text-center py-8 text-red-600">Workflow failed to load.</div>}>
                    <LazyWorkflowStages onOpenStage={handleOpenStage} />
                  </ErrorBoundary>
                } />
                <Route path="/tasks" element={<LazyTaskQueue />} />
                <Route path="/interventions" element={
                  <ErrorBoundary fallback={<div className="text-center py-8 text-red-600">Interventions failed to load.</div>}>
                    <LazyInterventions />
                  </ErrorBoundary>
                } />

                {/* Intelligence */}
                <Route path="/context-analytics" element={<LazyContextAnalytics />} />
                <Route path="/recommendations" element={<LazySystemRecommendations />} />
                <Route path="/cost" element={<LazyCostUsage />} />
                <Route path="/usage" element={<LazyUsageEfficiency />} />
                <Route path="/pgm" element={<LazyProgramPgm />} />
                <Route path="/pgm/discovery" element={<LazyDiscoveryPage />} />
                <Route path="/pgm/releases/:releaseId" element={<LazyReleaseWorkspace />} />
                <Route path="/control-tower" element={<LazyControlTower />} />

                {/* Admin (role-guarded) */}
                <Route path="/admin" element={
                  <ErrorBoundary fallback={<div className="text-center py-8 text-red-600">Administration failed to load.</div>}>
                    <RoleGuard allowedRoles={['admin']}>
                      <PermissionGuard requiredPermissions={['admin:access']}>
                        <LazyAdministration />
                      </PermissionGuard>
                    </RoleGuard>
                  </ErrorBoundary>
                } />
                <Route path="/admin/actors" element={<LazyActors />} />
                <Route path="/admin/workflows" element={<LazyWorkflows />} />
                <Route path="/admin/repos" element={<LazyRepos />} />
                <Route path="/admin/repos/sync" element={<LazyRepositorySyncStatus />} />
                <Route path="/admin/skills" element={<LazySkills />} />
                <Route path="/admin/cache" element={<LazyCacheManagement />} />
                <Route path="/admin/config" element={<LazyConfig />} />
                <Route path="/admin/audit-log" element={<LazyAudit />} />

                {/* Unauthorized */}
                <Route path="/unauthorized" element={
                  <div className="flex items-center justify-center h-full">
                    <div className="glass-card-premium w-full max-w-md p-12 text-center">
                      <div className="text-6xl mb-6">🔒</div>
                      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Access Denied</h1>
                      <p className="text-slate-500 dark:text-slate-400 mb-6">You don't have permission to access this page.</p>
                      <Button
                        type="button"
                        size="lg"
                        className="w-full rounded-2xl py-3 h-auto"
                        onClick={() => navigate('/dashboard')}
                      >
                        Return to Dashboard
                      </Button>
                    </div>
                  </div>
                } />

                {/* Legacy URL redirects */}
                <Route path="/sdlc" element={<Navigate to="/workflow" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          </div>
        </main>

        {/* Stage Workspace Modal */}
        <Suspense fallback={<LazyLoadingFallback message="Loading stage modal..." />}>
          <LazyStageModal
            isOpen={modalOpen}
            onClose={handleCloseModal}
            stageId={selectedStage}
            parentFilters={stageFilters}
          />
        </Suspense>

        {/* Toast Notifications.
            `closeButton` is app-wide so any toast (especially persistent error toasts
            such as a failed intervention resolution) can be read and manually dismissed
            rather than auto-vanishing (DEFECT-INTERVENTION-RESOLVE-VANISH-001). */}
        <Toaster position="top-right" closeButton />

        {/* Real-time Notification Components */}
        <InterventionNotification />
        <BrowserNotificationManager />
        <InAppNotificationPopup />

        {/* Idle warning modal */}
        <IdleWarningModal
          isOpen={showIdleWarning}
          remainingTime={remainingTime}
          onStayLoggedIn={handleStayLoggedIn}
          onLogoutNow={handleLogoutNow}
        />
      </div>
    </AuthGuard>
  );
}
